import { newRequestKey } from './request-key';
let csrfToken: string | null = null;

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

async function csrf(): Promise<string> {
  if (csrfToken) return csrfToken;
  const response = await fetch("/api/v1/admin/auth/csrf", { credentials: "include" });
  if (!response.ok) {
    if (response.status === 401) window.dispatchEvent(new Event('realrain-console-session-expired'));
    throw new ApiError(response.status, "管理会话已过期");
  }
  csrfToken = String((await response.json() as { csrfToken: string }).csrfToken);
  return csrfToken;
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("content-type")) headers.set("content-type", "application/json");
  if (!["GET", "HEAD"].includes(method)) {
    headers.set("x-csrf-token", await csrf());
    if (!headers.has("idempotency-key")) headers.set("idempotency-key", newRequestKey());
  }
  const response = await fetch(path, { ...init, method, headers, credentials: "include" });
  if (response.status === 401) {
    csrfToken = null;
    window.dispatchEvent(new Event('realrain-console-session-expired'));
    throw new ApiError(401, "管理会话已过期");
  }
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { error?: { message?: string }; message?: string };
    throw new ApiError(response.status, body.error?.message ?? body.message ?? "请求失败");
  }
  return response.status === 204 ? undefined as T : await response.json() as T;
}

export async function session(): Promise<{ authenticated: boolean; principalId?: string; roles?: string[] }> {
  const response = await fetch("/api/v1/admin/auth/me", { credentials: "include" });
  if (response.status === 401 || response.status === 403) return { authenticated: false };
  if (!response.ok) throw new ApiError(response.status, '暂时无法确认管理会话，请重试');
  return await response.json() as { authenticated: boolean; principalId?: string; roles?: string[] };
}

export async function logout(): Promise<void> {
  await Promise.allSettled([
    fetch('/api/pcas/auth/account/studio_admin/logout', { method: 'POST', credentials: 'same-origin', signal: AbortSignal.timeout(5000) }),
    fetch('/api/account/logout?surface=admin', { method: 'POST', credentials: 'same-origin', signal: AbortSignal.timeout(5000) }),
  ]);
  await api<void>("/api/v1/admin/auth/logout", { method: "POST" });
  csrfToken = null;
}
